// src/services/signalManager.js

import { KeyHelper } from '@privacyresearch/libsignal-protocol-typescript';
import { SignalProtocolAddress } from '@privacyresearch/libsignal-protocol-typescript';
import { signalStore } from './signalStore';
import api from './api'; // votre instance axios existante

const DEVICE_ID = 1; // simplifié : 1 appareil pour commencer

/**
 * Vérifie si l'utilisateur a déjà des clés Signal en local
 */
export async function hasLocalIdentity() {
  const identityKey = await signalStore.get('identityKey');
  const registrationId = await signalStore.get('registrationId');
  return !!(
    identityKey?.privKey &&
    identityKey?.pubKey &&
    registrationId
  );
}
/**
 * Génère toutes les clés Signal côté client et les stocke localement.
 * NE FAIT AUCUN APPEL RÉSEAU — tout est local.
 */
async function generateIdentity() {
  const registrationId = KeyHelper.generateRegistrationId();
  const identityKeyPair = await KeyHelper.generateIdentityKeyPair();
  // ✅ CORRIGÉ : generatePreKey (singulier) génère UNE clé à la fois
  // On boucle pour en générer 100
  const preKeys = [];
  for (let i = 1; i <= 100; i++) {
    const preKey = await KeyHelper.generatePreKey(i);
    preKeys.push(preKey);
  }

  // SignedPreKey : une clé signée par la clé d'identité
  const signedPreKey = await KeyHelper.generateSignedPreKey(
    identityKeyPair,
    1
  );

   
  // --- Stocker TOUT localement (clés privées + publiques) ---
  await signalStore.put('registrationId', registrationId);
  await signalStore.put('identityKey', identityKeyPair);

  // Stocker chaque PreKey privée localement
  for (const pk of preKeys) {
    await signalStore.put(`preKey_${pk.keyId}`, pk.keyPair);
  }

  // Stocker la SignedPreKey privée localement
  await signalStore.put('signedPreKey', {
    keyId: signedPreKey.keyId,
    keyPair: signedPreKey.keyPair,
    signature: signedPreKey.signature,
  });

//await signalStore.put('signedPreKey', recovered.signedPreKey); 
  await signalStore.put('provisioned', true);
  await signalStore.put('deviceId', DEVICE_ID);

  // --- Préparer les clés PUBLIQUES à envoyer au serveur ---
  return {
    registrationId,
    identityKeyPublic: bufferToBase64(identityKeyPair.pubKey),
    signedPreKey: {
      keyId: signedPreKey.keyId,
      publicKey: bufferToBase64(signedPreKey.keyPair.pubKey),
      signature: bufferToBase64(signedPreKey.signature),
    },
    preKeysPublic: preKeys.map((pk) => ({
      keyId: pk.keyId,
      publicKey: bufferToBase64(pk.keyPair.pubKey),
    })),
    deviceId: DEVICE_ID,
  };
}
/**
 * Publie les clés publiques vers le serveur.
 * Le serveur ne reçoit JAMAIS les clés privées.
 */
async function publishPublicKeys(publicKeys, { confirmReset = false } = {}) {
  await api.post('/api/signal/prekeys', { ...publicKeys, confirmReset });
}

/**
 * Vérifie si le serveur a déjà les clés publiques de cet utilisateur
 */
async function hasServerPublicKeys() {
  try {
    const res = await api.get('/api/signal/prekeys/status');
    return res.data.hasKeys;
  } catch {
    return false;
  }
}

/**
 * ORCHESTRATEUR — à appeler après chaque login réussi.
 * Vérifie, génère si besoin, publie si besoin.
 */
export async function provisionIfNeeded({ confirmReset = false } = {}) {
  const localKeys = await hasLocalIdentity();
  if (!localKeys) {
    const publicKeys = await generateIdentity();
    await publishPublicKeys(publicKeys, { confirmReset });
    return;
  }

  const identityKeyPair = await signalStore.get('identityKey');
  const registrationId = await signalStore.get('registrationId');

  const res = await api.get('/api/signal/prekeys/status');

  const serverHasKeys = res.data.hasKeys;
  const serverIdentityKey = res.data.identityKeyPublic;

  const localIdentityKey = bufferToBase64(identityKeyPair.pubKey);

  

  if (!serverHasKeys || localIdentityKey !== serverIdentityKey) {
    // republier les clés locales
  }
}

// --- Utilitaires ---

function bufferToBase64(buffer) {
  // Convertit un ArrayBuffer/Uint8Array en base64 string
  const bytes = buffer instanceof Uint8Array
    ? buffer
    : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export async function refreshPreKeysAfterRecovery(recovered) {
  const identityKeyPair = await signalStore.get('identityKey');
  const registrationId = await signalStore.get('registrationId');

  if (!identityKeyPair?.privKey || !identityKeyPair?.pubKey || !registrationId) {
    throw new Error('Identité incomplète : préclés non régénérées');
  }
  if (!recovered?.signedPreKey?.keyPair?.privKey) {
    throw new Error('SignedPreKey absente du backup');
  }

  // 1. On RESTAURE la signedPreKey du backup (même keyId, même clé)
  await signalStore.put('signedPreKey', {
    keyId: recovered.signedPreKey.keyId,
    keyPair: recovered.signedPreKey.keyPair,
    signature: recovered.signedPreKey.signature,
  });

  // 2. On régénère seulement les préclés, avec de NOUVEAUX ids
  //const startId = (await signalStore.get('nextPreKeyId')) || 1000; // voir note ci-dessous
const startId = (Math.floor(Date.now() / 1000) % 16000000) + 1;
  const preKeys = [];
  for (let i = 0; i < 100; i++) {
    preKeys.push(await KeyHelper.generatePreKey(startId + i));
  }
  for (const pk of preKeys) {
    await signalStore.put(`preKey_${pk.keyId}`, pk.keyPair);
  }
  await signalStore.put('nextPreKeyId', startId + 100);

  await signalStore.put('provisioned', true);
  await signalStore.put('deviceId', DEVICE_ID);

  // 3. Publication : la signedPreKey publiée est celle du backup
  const signed = recovered.signedPreKey;
  await publishPublicKeys({
    registrationId,
    identityKeyPublic: bufferToBase64(identityKeyPair.pubKey),
    signedPreKey: {
      keyId: signed.keyId,
      publicKey: bufferToBase64(signed.keyPair.pubKey),
      signature: bufferToBase64(signed.signature),
    },
    preKeysPublic: preKeys.map((pk) => ({
      keyId: pk.keyId,
      publicKey: bufferToBase64(pk.keyPair.pubKey),
    })),
    deviceId: DEVICE_ID,
  }, { confirmReset: true });
}

export async function diagnoseSignal(contactId) {
  const identity = await signalStore.get('identityKey');
  const signed = await signalStore.get('signedPreKey');
  const { data } = await api.get('/api/signal/prekeys/status');

  const out = {
    identiteLocale: !!identity?.privKey,
    identiteIdentiqueAuServeur: !!identity && bufferToBase64(identity.pubKey) === data.identityKeyPublic,
    signedPreKeyLocale: signed?.keyId ?? null,
    signedPreKeyServeur: data.signedPreKey?.keyId ?? null,
    signedPreKeyIdentique: !!signed?.keyPair && bufferToBase64(signed.keyPair.pubKey) === data.signedPreKey?.publicKey,
    preclesDisponiblesSurLeServeur: data.unusedPreKeys,
  };
  if (contactId) {
    const addr = new SignalProtocolAddress(String(contactId), 1).toString();
    out.sessionAvecLeContact = !!(await signalStore.get(`session_${addr}`));
  }
  console.table(out);
  return out;
}

export async function resetLocalSession(contactId) {
  const addr = new SignalProtocolAddress(String(contactId), 1).toString();
  await signalStore.remove(`session_${addr}`);
}

export async function ensureKeysPublished() {
  const identity = await signalStore.get('identityKey');
  const registrationId = await signalStore.get('registrationId');
  const signed = await signalStore.get('signedPreKey');
  if (!identity?.privKey || !registrationId || !signed?.keyPair?.privKey) {
    return { ok: false, reason: 'local_incomplete' };
  }

  const { data } = await api.get('/api/signal/prekeys/status');
  const localIdentity = bufferToBase64(identity.pubKey);

  // Une identité différente n'est JAMAIS écrasée automatiquement
  if (data.hasKeys && data.identityKeyPublic !== localIdentity) {
    return { ok: false, reason: 'identity_mismatch' };
  }

  const signedPub = bufferToBase64(signed.keyPair.pubKey);
  const signedOk = data.signedPreKey?.publicKey === signedPub && data.signedPreKey?.keyId === signed.keyId;
  const enoughPreKeys = (data.unusedPreKeys ?? 0) >= 20;
  if (data.hasKeys && signedOk && enoughPreKeys) return { ok: true, repaired: false };

  // Republier : identité et signedPreKey LOCALES font foi, plus un lot de préclés à identifiants neufs.
  // Les anciennes préclés privées restent en local : une session déjà en route reste ouvrable.
  const startId = (await signalStore.get('nextPreKeyId')) || 1000;
  const preKeys = [];
  for (let i = 0; i < 100; i++) preKeys.push(await KeyHelper.generatePreKey(startId + i));
  for (const pk of preKeys) await signalStore.put(`preKey_${pk.keyId}`, pk.keyPair);
  await signalStore.put('nextPreKeyId', startId + 100);

  await publishPublicKeys({
    registrationId,
    identityKeyPublic: localIdentity,
    signedPreKey: {
      keyId: signed.keyId,
      publicKey: signedPub,
      signature: bufferToBase64(signed.signature),
    },
    preKeysPublic: preKeys.map((pk) => ({ keyId: pk.keyId, publicKey: bufferToBase64(pk.keyPair.pubKey) })),
    deviceId: DEVICE_ID,
  });
  return { ok: true, repaired: true };
}

// Outils de diagnostic dans la console, uniquement si vous les activez :
//   localStorage.setItem('debugSignal', '1')   puis rechargez la page
if (typeof window !== 'undefined' && window.localStorage?.getItem('debugSignal') === '1') {
  window.diagnoseSignal = diagnoseSignal;
  window.resetLocalSession = resetLocalSession;
}
